import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { track } from "@vercel/analytics";
import InfoPage from "../InfoPage";
import ShareProgram from "../../components/ShareProgram";
import { usePageMeta, useCohort } from ".";
import { DRAFT_KEY } from "./RegistrationForm";
import { CADENCE_LABEL, DATES_LABEL, PAGE_DESCRIPTION, PAGE_PATH, formatMoney } from "./content";
import "./InnerCircle.sass";

const POLL_INTERVAL_MS = 4000;
const MAX_POLLS = 15;

const Confirmation = () => {
  usePageMeta("Your enrollment · Postaway Inner Circle", PAGE_DESCRIPTION);
  const { cohort } = useCohort();
  const supportEmail = cohort?.supportEmail || "jamie@postaway.com";

  const sessionId =
    typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("session_id") || "" : "";
  const [result, setResult] = useState(null);
  const [state, setState] = useState(sessionId ? "loading" : "missing");
  const [polls, setPolls] = useState(0);
  const [checking, setChecking] = useState(false);
  const [resendStatus, setResendStatus] = useState("");
  const [resending, setResending] = useState(false);
  const trackedRef = useRef(false);

  const check = useCallback(async () => {
    if (!sessionId) return;
    setChecking(true);
    try {
      const res = await fetch(`/api/inner-circle/checkout?session_id=${encodeURIComponent(sessionId)}`);
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setResult(data);
        setState(data.state);
      } else {
        setState("missing");
      }
    } catch (_) {
      setState("error");
    } finally {
      setChecking(false);
    }
  }, [sessionId]);

  useEffect(() => {
    check();
  }, [check]);

  useEffect(() => {
    if (state !== "processing" || polls >= MAX_POLLS) return undefined;
    const timer = window.setTimeout(() => {
      setPolls((n) => n + 1);
      check();
    }, POLL_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [state, polls, check]);

  useEffect(() => {
    if (state !== "paid" || trackedRef.current) return;
    trackedRef.current = true;
    try {
      sessionStorage.removeItem(DRAFT_KEY);
    } catch (_) {}
    try {
      track("ic_payment_confirmed_view");
    } catch (_) {}
  }, [state]);

  const resend = async () => {
    setResending(true);
    try {
      const res = await fetch("/api/inner-circle/resend-welcome", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId }),
      });
      const data = await res.json().catch(() => ({}));
      setResendStatus(data?.message || `Something went wrong. Contact ${supportEmail}.`);
    } catch (_) {
      setResendStatus(`Something went wrong. Contact ${supportEmail}.`);
    } finally {
      setResending(false);
    }
  };

  const amount = result ? formatMoney(result.amountCents, result.currency) : "";
  const supportLink = <a href={`mailto:${supportEmail}`}>{supportEmail}</a>;
  const backToRegistration = (
    <Link className="ic-btn primary" to={`${PAGE_PATH}#register`}>
      Return to registration
    </Link>
  );

  let content;
  if (state === "loading") {
    content = (
      <div className="ic-card ic-status-card" role="status">
        <div className="ic-spinner" aria-hidden="true" />
        Checking your enrollment…
      </div>
    );
  } else if (state === "paid") {
    content = (
      <>
        <div className="ic-eyebrow">Postaway Inner Circle</div>
        <h1 className="ic-confirm-title">You're in{result?.firstName ? `, ${result.firstName}` : ""}!</h1>
        <div className="ic-card" role="status">
          <p className="ic-card-text">
            <strong>{result?.cohortName}</strong>
            <br />
            {CADENCE_LABEL}, {DATES_LABEL} · Google Meet
            {amount ? (
              <>
                <br />
                Amount paid: {amount}
              </>
            ) : null}
          </p>
        </div>
        <p className="ic-card-text">
          Your welcome email with your private GroupMe invite is on its way to <strong>{result?.emailHint}</strong>.
          It can take a few minutes, so check your spam or promotions folder too.
        </p>
        <ol className="ic-confirm-steps">
          <li>Open your welcome email and join the Inner Circle GroupMe community.</li>
          <li>Save the Wednesday session dates on your calendar.</li>
          <li>Gather the photos and videos you already have. You'll put them to work in week one.</li>
        </ol>
        <button type="button" className="ic-btn secondary" onClick={resend} disabled={resending}>
          {resending ? "Sending…" : "Resend my welcome email"}
        </button>
        <p className="ic-hint" role="status" aria-live="polite">
          {resendStatus}
        </p>
        <p className="ic-hint">Questions? Email {supportLink}.</p>
        <div className="ic-section ic-share-band" style={{ width: "100%" }}>
          <h2 className="ic-section-title">Know a host or creator who'd love this?</h2>
          <ShareProgram
            path={PAGE_PATH}
            title="Postaway Inner Circle"
            text="I just joined Postaway Inner Circle, a hands-on social media program for hosts and creators."
            placement="confirmation"
          />
        </div>
      </>
    );
  } else if (state === "processing") {
    const gaveUp = polls >= MAX_POLLS;
    content = (
      <div className="ic-card ic-status-card" role="status" aria-live="polite">
        {!gaveUp && <div className="ic-spinner" aria-hidden="true" />}
        <h1 className="ic-card-title">We're confirming your payment</h1>
        <p className="ic-card-text">
          {gaveUp
            ? "Your payment is still processing. Some payment methods take a few business days to clear. We'll email you as soon as it's confirmed, and your GroupMe invite will follow."
            : "This usually takes a few seconds. Please keep this page open."}
        </p>
        <button type="button" className="ic-btn secondary" onClick={check} disabled={checking}>
          {checking ? "Checking…" : "Refresh status"}
        </button>
        <p className="ic-hint">Questions? Email {supportLink}.</p>
      </div>
    );
  } else if (state === "duplicate") {
    content = (
      <div className="ic-card ic-status-card" role="status">
        <h1 className="ic-card-title">You're already enrolled</h1>
        <p className="ic-card-text">
          It looks like this was a second payment for a spot you already have. We've let our team know so the extra
          payment can be refunded. Questions? Email {supportLink}.
        </p>
      </div>
    );
  } else if (state === "refunded") {
    content = (
      <div className="ic-card ic-status-card" role="status">
        <h1 className="ic-card-title">This enrollment was refunded</h1>
        <p className="ic-card-text">Questions? Email {supportLink}.</p>
      </div>
    );
  } else if (state === "failed" || state === "expired" || state === "open") {
    const title =
      state === "failed"
        ? "Your payment didn't go through"
        : state === "expired"
          ? "Your checkout session expired"
          : "Your payment isn't complete yet";
    content = (
      <div className="ic-card ic-status-card" role="status">
        <h1 className="ic-card-title">{title}</h1>
        <p className="ic-card-text">
          You haven't been charged for this attempt. Your registration details are saved, so you can pick up where you
          left off.
        </p>
        {backToRegistration}
        <p className="ic-hint">Need help? Email {supportLink}.</p>
      </div>
    );
  } else {
    content = (
      <div className="ic-card ic-status-card" role="status">
        <h1 className="ic-card-title">
          {state === "error" ? "We couldn't check your enrollment" : "We couldn't find that checkout"}
        </h1>
        <p className="ic-card-text">
          {state === "error"
            ? "Check your connection and try again."
            : "If you completed a payment, your welcome email is on its way."}{" "}
          Questions? Email {supportLink}.
        </p>
        {state === "error" ? (
          <button type="button" className="ic-btn secondary" onClick={check}>
            Try again
          </button>
        ) : (
          backToRegistration
        )}
      </div>
    );
  }

  const body = (
    <div className="ic-page">
      <div className="ic-confirm">{content}</div>
    </div>
  );

  return <InfoPage below={body} disableStamps />;
};

export default Confirmation;
